// Material Design 3 — палитра цветов для светлой и тёмной темы.
// Корпоративные цвета Jivo (https://www.jivo.ru): зелёный primary #2BAC06 + accent #49DA1F.
// Палитра приведена в соответствие с MD3 spec (M3 baseline palette).
import type { PaletteOptions } from '@mui/material';

/** Структура палитры MD3 (light + dark). */
export interface Md3Palette {
  primary: { main: string; onMain: string };
  secondary: { main: string; onMain: string };
  tertiary: { main: string; onMain: string };
  error: { main: string; onMain: string };
  surface: { default: string; variant: string };
  onSurface: { default: string; variant: string };
}

/** Светлая тема — основной фон белый, контрастный текст тёмный. */
export const lightPalette: Md3Palette = {
  primary: { main: '#2BAC06', onMain: '#FFFFFF' },
  secondary: { main: '#49DA1F', onMain: '#0E2B0A' },
  tertiary: { main: '#007AFF', onMain: '#FFFFFF' },
  error: { main: '#DC2626', onMain: '#FFFFFF' },
  surface: { default: '#FFFFFF', variant: '#F5F5F5' },
  onSurface: { default: '#1F1F1F', variant: '#6B7280' },
};

/** Тёмная тема — основной фон почти чёрный, контрастный текст светлый. */
export const darkPalette: Md3Palette = {
  primary: { main: '#49DA1F', onMain: '#0E2B0A' },
  secondary: { main: '#2BAC06', onMain: '#FFFFFF' },
  tertiary: { main: '#5EA8FF', onMain: '#0E2B0A' },
  error: { main: '#F87171', onMain: '#1A1A1A' },
  surface: { default: '#1A1A1A', variant: '#262626' },
  onSurface: { default: '#F5F5F5', variant: '#9CA3AF' },
};

/**
 * MUI v6 не имеет стандартного `tertiary` в `PaletteOptions`.
 * Module augmentation — расширяем стандартный палитр MUI нашим MD3 tertiary.
 */
declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary'];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
  }
}

/**
 * Конвертация MD3-палитры в MUI `PaletteOptions` для `createTheme`.
 * Связывает primary/secondary/tertiary/error/surface/onSurface с main/onMain.
 */
export function toMuiPalette(palette: Md3Palette): PaletteOptions {
  return {
    mode: palette === lightPalette ? 'light' : 'dark',
    primary: { main: palette.primary.main, contrastText: palette.primary.onMain },
    secondary: { main: palette.secondary.main, contrastText: palette.secondary.onMain },
    tertiary: { main: palette.tertiary.main, contrastText: palette.tertiary.onMain },
    error: { main: palette.error.main, contrastText: palette.error.onMain },
    background: {
      default: palette.surface.default,
      paper: palette.surface.variant,
    },
    text: {
      primary: palette.onSurface.default,
      secondary: palette.onSurface.variant,
    },
  };
}
